import { BASEMAP } from "@deck.gl/carto";
import { Deck } from "@deck.gl/core";
import maplibregl from "maplibre-gl";

const DEFAULT_MAP_PROPS = {
  layers: [],
  mapStyle: BASEMAP.POSITRON,
  defaultViewState: {
    longitude: -97.2,
    latitude: 44.33,
    zoom: 2,
  },
  controller: true,
  useDevicePixels: 2,
  getCursor: ({ isDragging, isHovering }) =>
    isDragging ? "grabbing" : isHovering ? "pointer" : "",
  glOptions: {
    preserveDrawingBuffer: true,
  },
};

const CANVAS_STYLE = {
  position: "absolute",
  left: 0,
  top: 0,
  width: "100%",
  height: "100%",
};

// Create canvas elements for map and deck
function createCanvas(props) {
  let { container = document.body } = props;

  if (typeof container === "string") {
    container = document.querySelector(container);
  }

  if (!container) {
    throw new Error("[DeckMap] container not found");
  }

  const containerStyle = window.getComputedStyle(container);
  if (containerStyle.position === "static") {
    container.style.position = "relative";
  }

  const mapCanvas = document.createElement("div");
  container.appendChild(mapCanvas);
  Object.assign(mapCanvas.style, CANVAS_STYLE);

  const deckCanvas = document.createElement("canvas");
  deckCanvas.classList.add("deck-canvas");
  deckCanvas.oncontextmenu = () => false; // this disables right click on map
  container.appendChild(deckCanvas);
  Object.assign(deckCanvas.style, CANVAS_STYLE);

  return { container, mapCanvas, deckCanvas };
}

function redrawMap(map) {
  // Force redraw now so jumpTo is reflected in the same frame as deck.
  // Typically resize()/jumpTo() wait for MapLibre's animation loop.
  if (map?.style) {
    // MapLibre 5 renamed _frame → _frameRequest (AbortController).
    if (map._frameRequest) {
      map._frameRequest.abort();
      map._frameRequest = null;
    } else if (map._frame) {
      map._frame.cancel();
      map._frame = null;
    }
    map._render(0);
  }
}

export default class DeckMap extends Deck {
  constructor(props = {}) {
    props = {
      ...DEFAULT_MAP_PROPS,
      onResize: () => {
        if (this._map) {
          this._map.resize();
        }
      },
      ...props,
    };
    const { mapCanvas, deckCanvas } = createCanvas(props);
    const viewState = props.viewState || props.defaultViewState;

    // Wire basemap sync into props BEFORE super() so Deck's first setProps
    // registers it. The previous setProps-wrapping approach never attached
    // after deck.gl 9.x changed initialization order, leaving MapLibre stuck
    // on the default world view while deck zoomed to the selected geography.
    const userOnBeforeRender = props.onBeforeRender;
    const ctx = { deck: null, map: null };
    props.onBeforeRender = (params) => {
      userOnBeforeRender?.(params);
      const { deck, map } = ctx;
      if (!deck || !map) return;
      const viewport = deck.getViewports()?.[0];
      if (!viewport) return;
      map.jumpTo({
        center: [viewport.longitude, viewport.latitude],
        zoom: viewport.zoom,
        bearing: viewport.bearing,
        pitch: viewport.pitch,
      });
      map.bounds = viewport.getBounds();
      redrawMap(map);
    };

    super({ canvas: deckCanvas, ...props });
    ctx.deck = this;

    const basemap = props.basemap;
    if (basemap) {
      this._map = basemap;
    } else {
      this._map = new maplibregl.Map({
        container: mapCanvas,
        style: props.mapStyle,
        interactive: false,
        center: [viewState.longitude, viewState.latitude],
        zoom: viewState.zoom,
        bearing: viewState.bearing || 0,
        pitch: viewState.pitch || 0,
        // MapLibre 5 moved WebGL options under canvasContextAttributes
        canvasContextAttributes: {
          preserveDrawingBuffer: true,
        },
      });
    }
    ctx.map = this._map;
  }
}
